// Contrôle de COHÉRENCE d'une animation : ce que l'élève voit doit dire la
// même chose que ce qu'il entend et lit. Pur : la validation (Studio et
// serveur) l'appelle sur le document compilé.
//
// Repère les incohérences qu'une IA laisse passer :
//   - un nombre affiché arrondi (`value` avec trop peu de décimales : 7,5
//     affiché « 8 ») ;
//   - deux valeurs pour une même longueur au même moment (la figure dit
//     AB = 8, la voix AB = 7,5) ;
//   - une figure qui n'est pas à l'échelle de ses nombres (AN = 2 dessiné plus
//     long, en proportion, que AB = 7,5) ;
//   - une longueur écrite contre le mauvais segment (« 6 » pour AB écrit au
//     milieu de [MB] : l'élève lit MB = 6) ;
//   - une affirmation que la figure dément : « (MN) ∥ (BC) » alors que les
//     droites ne sont pas parallèles sur le dessin (ou l'inverse), un angle
//     droit qui n'en est pas un, des points « alignés » qui ne le sont pas —
//     dans la narration, les textes affichés et les questions (consigne,
//     explication de la réponse).
//
// Les points sont les objets `point` nommés d'une lettre majuscule (« A »,
// « M' ») ; un segment [AB] est mesuré entre eux, à l'instant contrôlé (fin
// de chaque phrase, ouverture de chaque question).

import {
  distToSegment,
  formatFrenchNumber,
  objectFrames,
  sceneLayout,
  type LayoutItem,
} from './layout';
import { splitSentences } from './narration';
import type { JrafScene } from './schema/document';
import {
  sceneStateAt,
  type CompiledAnimation,
  type CompiledScene,
  type Point,
  type ResolvedObject,
} from './timeline';

export type CoherenceCode =
  | 'value_rounded'
  | 'value_conflict'
  | 'figure_scale'
  | 'figure_label'
  | 'figure_claim';

export interface CoherenceIssue {
  code: CoherenceCode;
  sceneIndex: number;
  /** objet en cause, s'il y en a un */
  objectId?: string;
  /** étape en cause (question) */
  stepIndex?: number;
  message: string;
}

/** Écart toléré entre les px par unité de deux longueurs d'une même figure. */
export const SCALE_TOLERANCE = 0.12;
/** Parallèles : angle toléré (degrés) ; « pas parallèles » : angle minimal. */
export const PARALLEL_TOLERANCE_DEG = 2.5;
export const NOT_PARALLEL_MIN_DEG = 0.8;
/** Angle droit, perpendiculaires : écart toléré à 90° (degrés). */
export const RIGHT_ANGLE_TOLERANCE_DEG = 2.5;
/** Une étiquette de longueur est lue pour le segment dont elle est à moins de… (px). */
export const LABEL_REACH = 36;

const POINT_NAME_RE = /^[A-Z](?:['′]|_?\d)?$/;
const SEGMENT_NAME_RE = /^[A-Z]['′]?[A-Z]['′]?$/;

// ------------------------------------------------------------- textes

/** Texte d'un `$…$` ou d'une formule ramené à une écriture simple, pour y chercher des faits. */
export function plainMath(text: string): string {
  return text
    .replace(/\$/g, '')
    .replace(/\{,\}/g, ',')
    .replace(/\\[,;: ]|~/g, ' ')
    .replace(/\\left|\\right/g, '')
    .replace(/\\times|\\cdot/g, '×')
    .replace(/\\parallel/g, '∥')
    .replace(/\\nparallel/g, '∦')
    .replace(/\\perp/g, '⊥')
    .replace(/\\neq|\\ne\b/g, '≠')
    .replace(/\s+/g, ' ');
}

/** Nom d'un segment sans ordre : « BA » et « AB » désignent [AB]. */
export function segmentKey(name: string): string {
  const m = /^([A-Z]['′]?)([A-Z]['′]?)$/.exec(name.replace(/′/g, "'"));
  if (!m) return name;
  return [m[1]!, m[2]!].sort().join('');
}

function pointsOf(name: string): [string, string] | null {
  const m = /^([A-Z]['′]?)([A-Z]['′]?)$/.exec(name.replace(/′/g, "'"));
  return m ? [m[1]!, m[2]!] : null;
}

function parseNumber(s: string): number {
  return Number(s.replace(/\s/g, '').replace(',', '.'));
}

export interface LengthFact {
  /** segment, sans ordre (« AB ») */
  key: string;
  /** comme écrit (« BA ») */
  name: string;
  value: number;
}

/** Longueurs énoncées dans un texte : « AB = 7,5 », « $AM=3$ » (pas « AB = 2 × 3 », pas « AB² = 9 »). */
export function lengthFacts(text: string): LengthFact[] {
  const s = plainMath(text);
  const out: LengthFact[] = [];
  const re =
    /(?<![A-Za-z{\\^_])([A-Z]['′]?[A-Z]['′]?)\s*=\s*(\d+(?:[.,]\d+)?)(?![\d.,]*\s*(?:[×x*/+\-^÷]|\\frac))/g;
  for (const m of s.matchAll(re)) {
    const value = parseNumber(m[2]!);
    if (!Number.isFinite(value) || value <= 0) continue;
    out.push({ key: segmentKey(m[1]!), name: m[1]!, value });
  }
  return out;
}

export type ClaimKind = 'parallel' | 'perpendicular' | 'right-angle' | 'aligned';

export interface GeometryClaim {
  kind: ClaimKind;
  negated: boolean;
  /** droites (deux points chacune), sommet + côtés, ou points alignés */
  points: string[];
  /** la phrase, pour le message */
  text: string;
}

const NEGATION_RE = /∦|\b(?:pas|non|jamais|ni)\b|n['’]est\b|ne\s+sont\b/i;
const QUESTION_RE = /\?\s*$|\bsavoir si\b|\best-ce que\b|-(?:elles|ils|elle|il)\b/i;

/** Affirmations géométriques d'une phrase (une question n'affirme rien). */
export function geometryClaims(sentence: string): GeometryClaim[] {
  const s = plainMath(sentence).trim();
  if (!s || QUESTION_RE.test(s)) return [];
  const negated = NEGATION_RE.test(s);
  const out: GeometryClaim[] = [];
  const lines = [...s.matchAll(/\(\s*([A-Z]['′]?)\s*([A-Z]['′]?)\s*\)/g)].map((m) => [
    m[1]!,
    m[2]!,
  ]);
  const twoLines =
    lines.length >= 2 && segmentKey(lines[0]!.join('')) !== segmentKey(lines[1]!.join(''))
      ? [...lines[0]!, ...lines[1]!]
      : null;
  if (twoLines && /parall|∥|∦|\/\//i.test(s))
    out.push({ kind: 'parallel', negated, points: twoLines, text: s });
  if (twoLines && /perpendiculaire|⊥/i.test(s))
    out.push({ kind: 'perpendicular', negated, points: twoLines, text: s });
  const right =
    /\b([A-Z]['′]?)([A-Z]['′]?)([A-Z]['′]?)\b[^.;]*?\brectangle\s+en\s+([A-Z]['′]?)/.exec(s);
  if (right) {
    const tri = [right[1]!, right[2]!, right[3]!];
    const at = right[4]!;
    if (tri.includes(at)) {
      const others = tri.filter((p) => p !== at);
      out.push({ kind: 'right-angle', negated, points: [at, ...others], text: s });
    }
  }
  const aligned =
    /\b([A-Z]['′]?)\s*,\s*([A-Z]['′]?)\s*(?:,|et)\s*([A-Z]['′]?)\s+(?:sont|soient)\s+(?:bien\s+)?align/.exec(
      s,
    );
  if (aligned)
    out.push({
      kind: 'aligned',
      negated,
      points: [aligned[1]!, aligned[2]!, aligned[3]!],
      text: s,
    });
  return out;
}

// ------------------------------------------------------------- figure

interface Figure {
  /** points nommés visibles : nom → position (repère du canevas) */
  points: Map<string, Point>;
  /** segments élémentaires dessinés (entre deux points consécutifs d'un trait) */
  elementary: { key: string; name: string; a: Point; b: Point }[];
  layout: LayoutItem[];
}

const VISIBLE = 0.15;

function figureAt(anim: CompiledAnimation, scene: CompiledScene, t: number): Figure {
  const state = sceneStateAt(scene, t, { reducedMotion: true });
  const frames = objectFrames(anim, state);
  const points = new Map<string, Point>();
  for (const o of anim.objects) {
    if (o.type !== 'point') continue;
    const f = frames.get(o.id);
    const s = state[o.id];
    if (!f || !s || f.opacity < VISIBLE) continue;
    const label = (s.label ?? o.label ?? '').trim();
    if (!POINT_NAME_RE.test(label)) continue;
    points.set(label.replace(/′/g, "'"), f.xf({ x: o.x, y: o.y }));
  }
  const layout = sceneLayout(anim, scene, t);
  const elementary: Figure['elementary'] = [];
  const seen = new Set<string>();
  for (const it of layout) {
    if (!it.segments || it.role) continue;
    for (const sg of it.segments) {
      const len = Math.hypot(sg.b.x - sg.a.x, sg.b.y - sg.a.y);
      if (len < 4) continue;
      const on = [...points]
        .filter(([, p]) => distToSegment(p, sg.a, sg.b) <= 3)
        .map(([name, p]) => ({
          name,
          p,
          u:
            ((p.x - sg.a.x) * (sg.b.x - sg.a.x) + (p.y - sg.a.y) * (sg.b.y - sg.a.y)) / (len * len),
        }))
        .sort((x, y) => x.u - y.u);
      for (let i = 0; i + 1 < on.length; i++) {
        const key = segmentKey(on[i]!.name + on[i + 1]!.name);
        if (seen.has(key)) continue;
        seen.add(key);
        elementary.push({ key, name: on[i]!.name + on[i + 1]!.name, a: on[i]!.p, b: on[i + 1]!.p });
      }
    }
  }
  return { points, elementary, layout };
}

function angleBetween(a: Point, b: Point, c: Point, d: Point): number {
  const u = { x: b.x - a.x, y: b.y - a.y };
  const v = { x: d.x - c.x, y: d.y - c.y };
  const nu = Math.hypot(u.x, u.y);
  const nv = Math.hypot(v.x, v.y);
  if (nu < 1e-6 || nv < 1e-6) return Number.NaN;
  const cos = Math.abs((u.x * v.x + u.y * v.y) / (nu * nv));
  return (Math.acos(Math.min(1, cos)) * 180) / Math.PI; // 0 … 90 : angle entre droites
}

/** Ce que la figure dit d'une affirmation : null si elle ne peut pas trancher. */
function claimHolds(
  claim: GeometryClaim,
  pts: Map<string, Point>,
): { ok: boolean; measure: string } | null {
  const p = claim.points.map((n) => pts.get(n.replace(/′/g, "'")));
  if (p.some((x) => !x)) return null;
  const [a, b, c, d] = p as Point[];
  const r1 = (x: number) => Math.round(x * 10) / 10;
  switch (claim.kind) {
    case 'parallel': {
      const ang = angleBetween(a!, b!, c!, d!);
      if (Number.isNaN(ang)) return null;
      const ok = claim.negated ? ang >= NOT_PARALLEL_MIN_DEG : ang <= PARALLEL_TOLERANCE_DEG;
      return { ok, measure: `sur la figure, l'angle entre elles est de ${r1(ang)}°` };
    }
    case 'perpendicular': {
      const ang = angleBetween(a!, b!, c!, d!);
      if (Number.isNaN(ang)) return null;
      const off = 90 - ang;
      const ok = claim.negated ? off > RIGHT_ANGLE_TOLERANCE_DEG : off <= RIGHT_ANGLE_TOLERANCE_DEG;
      return { ok, measure: `sur la figure, elles font un angle de ${r1(ang)}°` };
    }
    case 'right-angle': {
      // sommet a, côtés vers b et c
      const ang = angleBetween(a!, b!, a!, c!);
      if (Number.isNaN(ang)) return null;
      const off = 90 - ang;
      const ok = claim.negated ? off > RIGHT_ANGLE_TOLERANCE_DEG : off <= RIGHT_ANGLE_TOLERANCE_DEG;
      return { ok, measure: `sur la figure, l'angle en ${claim.points[0]} mesure ${r1(ang)}°` };
    }
    case 'aligned': {
      const len = Math.hypot(c!.x - a!.x, c!.y - a!.y);
      const off = Math.min(
        distToSegment(b!, a!, c!),
        distToSegment(a!, b!, c!),
        distToSegment(c!, a!, b!),
      );
      const ok = claim.negated ? off > 3 : off <= Math.max(2, 0.01 * len);
      return {
        ok,
        measure: `sur la figure, un des points est à ${Math.round(off)} px de la droite`,
      };
    }
  }
}

function claimLabel(c: GeometryClaim): string {
  const [a, b, x, y] = c.points;
  switch (c.kind) {
    case 'parallel':
      return `(${a}${b}) ${c.negated ? "n'est pas parallèle à" : '∥'} (${x}${y})`;
    case 'perpendicular':
      return `(${a}${b}) ${c.negated ? "n'est pas perpendiculaire à" : '⊥'} (${x}${y})`;
    case 'right-angle':
      return `${c.negated ? 'pas ' : ''}rectangle en ${a}`;
    case 'aligned':
      return `${c.points.join(', ')} ${c.negated ? 'pas ' : ''}alignés`;
  }
}

// ------------------------------------------------------------- nombres

/** Ce qu'affiche un `value` (comme le lecteur) et sa vraie valeur. */
function shownValue(o: Extract<ResolvedObject, { type: 'value' }>, v: number): number {
  return parseNumber(formatFrenchNumber(v, o.decimals).replace(/\s/g, ''));
}

const UNIT_RE = /^(?:mm|cm|dm|m|km)$/;

/** Une étiquette numérique de longueur : « 7,5 », « $6$ », « 4 cm » (pas « 30° »). */
function numericLabel(text: string): number | null {
  const m = /^\s*(\d+(?:[.,]\d+)?)\s*([a-zA-Z]{0,2})\s*$/.exec(plainMath(text));
  if (!m) return null;
  if (m[2] && !UNIT_RE.test(m[2])) return null;
  const v = parseNumber(m[1]!);
  return Number.isFinite(v) && v > 0 ? v : null;
}

// ------------------------------------------------------------- contrôle

interface Instant {
  t: number;
  /** phrases de la narration dites jusque-là */
  said: number;
  /** question qui s'ouvre (index de l'étape) */
  gate?: number;
}

function instants(scene: CompiledScene): Instant[] {
  const out: Instant[] = [];
  const starts = scene.sentenceStartsMs;
  const end = scene.schedule.endMs;
  starts.forEach((s, k) => {
    const next = starts[k + 1] ?? Math.max(end, s);
    out.push({ t: Math.max(s, next - 1), said: k + 1 });
  });
  for (const st of scene.schedule.steps)
    if (st.gate) {
      const said = starts.filter((s) => s <= st.startMs).length;
      out.push({ t: st.startMs, said, gate: st.index });
    }
  return out.sort((a, b) => a.t - b.t);
}

function fmt(v: number): string {
  return formatFrenchNumber(v, Math.min(3, decimalsNeeded(v)));
}

/** Contrôle de cohérence de toute l'animation (document validé, compilé). */
export function checkConsistency(anim: CompiledAnimation): CoherenceIssue[] {
  const doc = anim.doc;
  const issues: CoherenceIssue[] = [];
  const seen = new Set<string>();
  const report = (key: string, issue: CoherenceIssue) => {
    if (seen.has(key)) return;
    seen.add(key);
    issues.push(issue);
  };
  const paramLabel = new Map(
    doc.parameters.map((p) => [p.id, p.type === 'number' ? (p.label ?? '') : '']),
  );

  /** Segment qu'une étiquette `value` est censée donner (paramètre « AB », `mentions`). */
  const intendedOf = (o: ResolvedObject): string | null => {
    const raw = doc.objects.find((x) => x.id === o.id);
    if (raw?.type === 'value' && typeof raw.expr === 'string') {
      const label = paramLabel.get(raw.expr.trim());
      if (label && SEGMENT_NAME_RE.test(label)) return label;
    }
    const named = raw?.mentions?.find((m) => SEGMENT_NAME_RE.test(m));
    return named ?? null;
  };

  anim.scenes.forEach((scene, si) => {
    const title = scene.scene.title;
    const where = `scène « ${title} »`;
    const sentences = splitSentences(scene.scene.narration.text);
    const sentenceFacts = sentences.map((s) => lengthFacts(s));
    const sentenceClaims = sentences.map((s) => geometryClaims(s));

    // échelle : on garde le dernier constat de la scène (le plus complet)
    let scaleIssue: CoherenceIssue | null = null;
    for (const at of instants(scene)) {
      const fig = figureAt(anim, scene, at.t);
      const state = sceneStateAt(scene, at.t, { reducedMotion: true });
      const visible = (id: string) => fig.layout.some((it) => it.id === id && it.text && !it.role);

      // faits : narration dite jusque-là (la dernière valeur dite l'emporte)
      const facts = new Map<string, { name: string; value: number; source: string }>();
      sentenceFacts.slice(0, at.said).forEach((fs, k) =>
        fs.forEach((f) =>
          facts.set(f.key, {
            name: f.name,
            value: f.value,
            source: `la narration (phrase ${k + 1})`,
          }),
        ),
      );
      const conflicts: string[] = [];
      const addFact = (f: LengthFact, source: string, override = true) => {
        const prev = facts.get(f.key);
        if (prev && Math.abs(prev.value - f.value) > 1e-9)
          conflicts.push(
            `${source} dit ${f.name} = ${fmt(f.value)}, ${prev.source} ${prev.name} = ${fmt(prev.value)}`,
          );
        if (!prev || override) facts.set(f.key, { name: f.name, value: f.value, source });
      };

      // textes et formules affichés
      for (const o of anim.objects) {
        if ((o.type !== 'text' && o.type !== 'formula') || !visible(o.id)) continue;
        const s = state[o.id];
        const content = o.type === 'text' ? (s?.label ?? o.text) : (s?.tex ?? o.tex);
        for (const f of lengthFacts(content)) addFact(f, `« ${o.id} »`);
      }

      // étiquettes de longueur (nombres affichés près d'un segment)
      for (const o of anim.objects) {
        if (!visible(o.id)) continue;
        const s = state[o.id];
        let shown: number | null = null;
        if (o.type === 'value' && s?.value !== null && s?.value !== undefined) {
          const v = s.value;
          shown = shownValue(o, v);
          // une valeur approchée s'annonce : « ≈ 1,41 »
          const approx = /[≈~]|environ/.test(`${o.prefix ?? ''}${o.suffix ?? ''}`);
          if (Math.abs(shown - v) > 1e-9 && !approx) {
            const oi = doc.objects.findIndex((x) => x.id === o.id);
            report(`round|${o.id}`, {
              code: 'value_rounded',
              sceneIndex: si,
              objectId: o.id,
              message: `« ${o.id} » affiche ${formatFrenchNumber(v, o.decimals)} alors qu'il vaut ${fmt(v)} (${where}) : mets "decimals": ${Math.min(6, decimalsNeeded(v))} (objects[${oi}]) — ou, pour une valeur approchée voulue, "prefix": "≈ "`,
            });
          }
        } else if (o.type === 'text') {
          shown = numericLabel(s?.label ?? o.text);
        }
        if (shown === null) continue;
        const ink = fig.layout.find((it) => it.id === o.id && it.text && !it.role)?.text?.ink;
        if (!ink) continue;
        const c = { x: (ink.x0 + ink.x1) / 2, y: (ink.y0 + ink.y1) / 2 };
        // segment le long duquel on lit le nombre
        let perceived: { key: string; name: string; d: number } | null = null;
        for (const e of fig.elementary) {
          const len2 = (e.b.x - e.a.x) ** 2 + (e.b.y - e.a.y) ** 2;
          const u = ((c.x - e.a.x) * (e.b.x - e.a.x) + (c.y - e.a.y) * (e.b.y - e.a.y)) / len2;
          if (u < 0.1 || u > 0.9) continue;
          const d = distToSegment(c, e.a, e.b);
          if (d > LABEL_REACH) continue;
          if (!perceived || d < perceived.d) perceived = { key: e.key, name: e.name, d };
        }
        const intended = intendedOf(o);
        if (intended && perceived && segmentKey(intended) !== perceived.key) {
          const pk = perceived.name;
          report(`lbl|${o.id}|${perceived.key}`, {
            code: 'figure_label',
            sceneIndex: si,
            objectId: o.id,
            message: `« ${o.id} » donne ${intended} = ${fmt(shown)}, mais il est écrit contre [${pk}] : l'élève lit ${pk} = ${fmt(shown)} (${where}). Écris « ${intended} = ${fmt(shown)} » en texte, ou affiche la longueur de [${pk}]`,
          });
        }
        const name = intended ?? perceived?.name;
        // ce que dit la voix reste la référence : une étiquette différente est un conflit
        if (name) addFact({ key: segmentKey(name), name, value: shown }, `« ${o.id} »`, false);
      }

      // question qui s'ouvre : consigne et explication
      let gateStep: JrafScene['steps'][number] | undefined;
      if (at.gate !== undefined) {
        gateStep = scene.scene.steps[at.gate];
        if (gateStep?.action === 'question.ask') {
          const src = `la question « ${gateStep.id} »`;
          for (const f of lengthFacts(gateStep.prompt)) addFact(f, src);
          for (const f of lengthFacts(gateStep.explanation ?? ''))
            addFact(f, `l'explication de ${src}`);
          // « Combien vaut MN ? » : la bonne réponse est une longueur de la figure
          const asked = /\bCombien (?:vaut|mesure|fait)\s+\$?([A-Z]['′]?[A-Z]['′]?)\$?/.exec(
            plainMath(gateStep.prompt),
          );
          const answer = numericLabel(gateStep.choices[gateStep.correct] ?? '');
          if (asked && answer !== null)
            addFact(
              { key: segmentKey(asked[1]!), name: asked[1]!, value: answer },
              `la bonne réponse de ${src}`,
            );
        }
      }

      for (const c of conflicts)
        report(`conf|${si}|${c}`, {
          code: 'value_conflict',
          sceneIndex: si,
          stepIndex: at.gate,
          message: `deux valeurs pour une même longueur (${where}) : ${c}. Une seule valeur, partout (figure, voix, formules)`,
        });

      // échelle : toutes les longueurs connues, mêmes px par unité
      const measured: { name: string; value: number; px: number; ratio: number }[] = [];
      for (const f of facts.values()) {
        const pp = pointsOf(f.name);
        if (!pp) continue;
        const a = fig.points.get(pp[0].replace(/′/g, "'"));
        const b = fig.points.get(pp[1].replace(/′/g, "'"));
        if (!a || !b) continue;
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len < 4) continue;
        measured.push({ name: f.name, value: f.value, px: len, ratio: len / f.value });
      }
      if (measured.length >= 2) {
        const ratios = measured.map((m) => m.ratio).sort((a, b) => a - b);
        if (ratios[ratios.length - 1]! / ratios[0]! > 1 + SCALE_TOLERANCE) {
          const u = ratios[Math.floor((ratios.length - 1) / 2)]!;
          const detail = measured
            .map(
              (m) =>
                `${m.name} = ${fmt(m.value)} → ${Math.round(m.px)} px (${Math.round(m.ratio)} px par unité${Math.abs(m.ratio / u - 1) > SCALE_TOLERANCE / 2 ? `, il faudrait ${Math.round(m.value * u)} px` : ''})`,
            )
            .join(' ; ');
          scaleIssue = {
            code: 'figure_scale',
            sceneIndex: si,
            stepIndex: at.gate,
            message: `la figure n'est pas à l'échelle de ses nombres (${where}) : ${detail}. Place chaque point avec la même unité (≈ ${Math.round(u)} px par unité) ; si les nombres changent, la figure doit changer aussi`,
          };
        }
      }

      // affirmations : narration dite pendant la phrase, textes affichés, question
      const claims: { claim: GeometryClaim; source: string }[] = [];
      const k = at.said - 1;
      if (at.gate === undefined && k >= 0)
        for (const claim of sentenceClaims[k] ?? [])
          claims.push({ claim, source: `la narration (phrase ${k + 1})` });
      for (const o of anim.objects) {
        if ((o.type !== 'text' && o.type !== 'formula') || !visible(o.id)) continue;
        const s = state[o.id];
        const content = o.type === 'text' ? (s?.label ?? o.text) : (s?.tex ?? o.tex);
        for (const claim of geometryClaims(content)) claims.push({ claim, source: `« ${o.id} »` });
      }
      if (gateStep?.action === 'question.ask') {
        const shownAnswer = gateStep.choices[gateStep.correct] ?? '';
        for (const sentence of splitSentences(gateStep.explanation ?? ''))
          for (const claim of geometryClaims(sentence))
            claims.push({ claim, source: `l'explication de la question « ${gateStep.id} »` });
        for (const claim of geometryClaims(shownAnswer))
          claims.push({ claim, source: `la bonne réponse de « ${gateStep.id} »` });
      }
      for (const { claim, source } of claims) {
        const res = claimHolds(claim, fig.points);
        if (!res || res.ok) continue;
        report(`claim|${si}|${claim.kind}|${claim.negated}|${claim.points.join('')}`, {
          code: 'figure_claim',
          sceneIndex: si,
          stepIndex: at.gate,
          message: `${source} affirme ${claimLabel(claim)}, mais ${res.measure} (${where}). Redessine la figure pour qu'elle le montre${claim.kind === 'parallel' && !claim.negated ? ' (place les points avec les mêmes rapports)' : ''}`,
        });
      }
    }
    if (scaleIssue) report(`scale|${si}`, scaleIssue);
  });
  return issues;
}

function decimalsNeeded(v: number): number {
  for (let d = 0; d <= 6; d++) if (Math.abs(Number(v.toFixed(d)) - v) < 1e-9) return d;
  return 6;
}
